import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withRouterConfig, withPreloading, TitleStrategy } from '@angular/router';
import { routes } from './app-routes';
import { IdlePreloadStrategy } from './routes/strategies/idle-preload.strategy';
import { AppTitleStrategy } from './routes/strategies/app-title-strategy';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { DynamicComponentService } from './services/dynamic-component.service';
import { RequestInterceptor } from './api-services/interceptors/request-interceptor';
import { ResponseInterceptor } from './api-services/interceptors/response-interceptor';
import { MapAttachmentBinderPipe } from './helpers/pipes/mapAttachment-binder.pipe';
import {
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService
} from '@azure/msal-angular';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ResponseInterceptor,
      multi: true
    },
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          // Dark mode is controlled explicitly via the `.app-dark` class that
          // ThemeService toggles on <html> (instead of following the OS), so the
          // in-app light/dark toggle drives PrimeNG's surface scheme too.
          darkModeSelector: '.app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'bootstrap, primeng, overrides'
          }
        }
      }
    }),
    provideZonelessChangeDetection(),
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'always' }), withPreloading(IdlePreloadStrategy)),
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    MessageService,
    DynamicComponentService,
    MapAttachmentBinderPipe,
    {
      provide: MSAL_INSTANCE,
      useFactory: () =>
        new PublicClientApplication({
          auth: {
            clientId: environment.azureClientId,
            authority: `https://login.microsoftonline.com/${environment.azureTenentId}`,
            redirectUri: window.location.origin + environment.azureRedirectUrl
          },
          cache: {
            cacheLocation: 'localStorage'
          }
        })
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: () => ({
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: ['openid', 'profile', 'email']
        }
      })
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: () => ({
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map()
      })
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ]
};
